<script>
  import { onDestroy } from "svelte";
  import { fade, fly, slide } from "svelte/transition";
  import { RADICAL, KANJI, VOCABULARY } from "./constants.js";
  import {
    sortStore,
    flashCardModeStore,
    descriptionWindowStore
  } from "./stores.js";
  import Check from "./icons/Check.svelte";
  import HelpIcon from "./icons/HelpIcon.svelte";
  export let subject;

  let flashCardFlipped = false;

  $: applyFontSize = characters => {
    if (!characters) return "inherit";
    switch (characters.length) {
      case 1:
        return "character-1";
      case 2:
        return "character-2";
      case 3:
        return "character-3";
      case 4:
        return "character-4";
      case 5:
        return "character-5";
    }
  };

  $: getSubjectBackgroundClassName = type => {
    let className;
    if (type === RADICAL) className = "radical-bg";
    if (type === KANJI) className = "kanji-bg";
    if (type === VOCABULARY) className = "vocabulary-bg";
    return `left ${className}`;
  };

  function triggerCard() {
    if (!$flashCardModeStore.active) return;
    flashCardFlipped = !flashCardFlipped;
  }

  const unsubscribe = flashCardModeStore.subscribe(flashCardModeStore => {
    if (flashCardModeStore.active) flashCardFlipped = false;
  });

  function updateDescriptionWindow(event) {
    event.stopPropagation();
    descriptionWindowStore.set({
      ...$descriptionWindowStore,
      active: true,
      characters: subject.characters,
      subjectType: subject.type,
      meaning: subject.meaning,
      meaningMnemonic: subject.meaning_mnemonic,
      readingMnemonic: subject.reading_mnemonic
    });
  }

  onDestroy(unsubscribe);
</script>

<style>
  li {
    align-items: center;
    background: var(--white);
    border-radius: 3px;
    box-shadow: var(--card-box-shadow);
    display: flex;
    list-style: none;
    justify-content: center;
    height: 100px;
    overflow: hidden;
    transition: all 0.3s;
  }

  li.hoverable:hover {
    box-shadow: var(--card-box-shadow-hover);
    cursor: pointer;
    transform: translate(0, -2px);
  }

  .left {
    flex: 1.5;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #333;
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
    color: var(--white);
    filter: brightness(1);
    height: 100%;
    white-space: nowrap;
    position: relative;
    transition: all 0.3s;
    z-index: 100;
  }

  li.hoverable:hover .left {
    filter: brightness(1.065);
  }

  .radical-bg {
    background: var(--blue);
    background-image: var(--blue-gradient);
  }

  .kanji-bg {
    background: var(--pink);
    background-image: var(--pink-gradient);
  }

  .vocabulary-bg {
    background: var(--purple);
    background-image: var(--purple-gradient);
  }

  .right {
    display: flex;
    flex: 2;
    flex-direction: column;
    height: 100%;
    justify-content: center;
    max-width: 180px;
    position: relative;
    transition: all 0.4s ease-in-out;
    white-space: nowrap;
  }

  .right.closed {
    flex: 0;
    padding-left: 0;
    max-width: 0;
  }

  .content-wrapper {
    padding-left: 10px;
    position: relative;
  }

  .big {
    font-size: 4rem;
    margin-bottom: 0;
  }

  .medium {
    font-size: 2.2rem;
    margin-bottom: 0;
  }

  .text {
    font-size: 1.25rem;
    margin-top: 0.7rem;
  }

  .reduced {
    font-size: 1.8rem;
  }

  .reduced-more {
    font-size: 1.7rem;
  }

  p {
    font-size: 1rem;
    margin: 0;
  }

  .image-wrap {
    filter: invert(1);
    height: 64px;
    width: 64px;
  }

  .character-1 {
    font-size: 3.5rem;
  }

  .character-2 {
    font-size: 2.7rem;
  }

  .character-3 {
    font-size: 2.2rem;
  }

  .character-4 {
    font-size: 1.8rem;
  }

  .character-5 {
    font-size: 1.4rem;
  }

  .closed small {
    right: -3rem;
    transition: right 0.3s ease 0s;
  }

  small {
    bottom: 0.3rem;
    color: #61ad6e;
    right: 0.5rem;
    position: absolute;
    font-size: 0.7rem;
    display: flex;
    align-items: center;
    transition: right 0.3s ease 0.3s;
  }

  .correct-percentage {
    margin-left: 0.25rem;
  }

  img {
    width: 100%;
  }

  .strong {
    font-family: var(--font-family-heading);
    font-size: 1rem;
    font-weight: 500;
  }

  .help-button {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
  }

  @media screen and (max-width: 1024px) {
    li.hoverable:hover {
      box-shadow: var(--card-box-shadow);
      cursor: pointer;
      transform: translate(0, 0);
    }

    .left {
      filter: none;
    }

    li.hoverable:hover .left {
      filter: none;
    }
  }

  @media screen and (max-width: 768px) {
    .radical-bg {
      background: var(--blue);
    }

    .kanji-bg {
      background: var(--pink);
    }

    .vocabulary-bg {
      background: var(--purple);
    }
  }
</style>

<li class:hoverable={$flashCardModeStore.active} on:click={triggerCard}>
  <div class={getSubjectBackgroundClassName(subject.type)}>
    <div
      class={applyFontSize(subject.characters)}
      class:image-wrap={!subject.characters}
      data-text="kanji">
      {#if subject.characters}
        {subject.characters}
      {:else}
        <img src={subject.image_url} alt="radical character image" />
      {/if}
    </div>
  </div>
  <div
    class="right"
    class:closed={$flashCardModeStore.active && !flashCardFlipped}>
    <div class="content-wrapper">
      <p class="strong" data-text="meaning">{subject.meaning}</p>
      {#if subject.reading}
        <p class="text" data-text="reading">{subject.reading}</p>
      {/if}
    </div>
    {#if $sortStore === 'frequently missed'}
      <small>
        <Check />
        <span class="correct-percentage">{subject.percentage_correct}%</span>
      </small>
    {/if}
    <button class="help-button" on:click={updateDescriptionWindow}>
      <HelpIcon />
    </button>
  </div>
</li>
